import React, { Component } from 'react';
import { compose } from 'redux';
import { Redirect, Link } from 'react-router-dom';
import { connect } from 'react-redux';
import qs from 'qs';
import { formValueSelector, change } from 'redux-form';

import {
  updateCustomPlanPricingStatusPending,
  deleteCustomPlanPricingStatusPending,
  getLoggedInUser,
} from 'reducers';
import {
  updateCustomPlanPricing,
  deleteCustomPlanPricing,
  fetchCustomPlanPricingList,
} from 'actions/customPlans';
import ModalContainer from 'react-modal-promise';
import Button from 'react-bootstrap/lib/Button';
import withCustomPlanPricing from 'decorators/withCustomPlanPricing';
import Pagination from 'components/Pagination/Pagination';
import CustomPlanPricingList from 'components/CustomPlanPricingList';
import CustomPlanPricingDeleteModal from 'components/CustomPlanPricingDeleteModal';
import CustomPlanPricingUpdateModal from 'components/CustomPlanPricingUpdateModal';
import FormError from 'components/FormError';
import Spinner from 'components/Spinner';
import { exportToCsv } from 'helpers/csv';
import { formatDateForAPI, prettyKey } from 'helpers/formatters';
import { fetchCustomPlanPricingList as fetchCustomPlanPricingListRequest } from 'api';
import CustomPlanPricingFilters from 'components/CustomPlanPricingFilters';
import { BIG_PAGE_SIZE } from '../../constants';
import { can, permActions } from 'permissions';

class CustomPlanPricing extends Component {
  state = {
    isModalOpen: false,
    isUpdateModalOpen: false,
    currentCustomPlan: null,
    csvError: '',
    csvLoading: false,
    updateError: null,
  };

  closeUpdateModal = () =>
    this.setState({ isUpdateModalOpen: false, updateError: null });

  handleOpenDeleteModal = async (customPlanPricing) => {
    this.clearError();
    if (!customPlanPricing) {
      return;
    }

    const { deleteCustomPlanPricing, fetchCustomPlanPricingListWithFilters } =
      this.props;
    const { id } = customPlanPricing;

    try {
      this.setState({ isModalOpen: true });
      const confirmResult = await CustomPlanPricingDeleteModal({
        open: this.state.isModalOpen,
        customPlanPricing,
      });

      if (confirmResult) {
        this.setState({ isEditState: false });

        await deleteCustomPlanPricing(id);
        await fetchCustomPlanPricingListWithFilters();
      }
    } catch (error) {
      this.setState({ isModalOpen: false, error: { message: error.message } });
    }
  };

  handleOpenUpdateModal = async (customPlanPricing) => {
    this.clearError();
    if (!customPlanPricing) {
      return;
    }
    this.setState({
      isUpdateModalOpen: true,
      currentCustomPlan: customPlanPricing,
    });
  };

  handleUpdatePlan = async (values) => {
    const { currentCustomPlan } = this.state;
    const { updateCustomPlanPricing, fetchCustomPlanPricingListWithFilters } =
      this.props;
    const { id } = currentCustomPlan;

    try {
      this.setState({ isEditState: false });
      await updateCustomPlanPricing(id, values);
      await fetchCustomPlanPricingListWithFilters();
      this.setState({ isUpdateModalOpen: false, currentCustomPlan: null });
    } catch (error) {
      this.setState({ updateError: { message: error.message } });
    }
  };

  handleApplyFilters = (values) => {
    const { history } = this.props;
    let query = { page: 1 };
    Object.entries(values).forEach(([key, value]) => {
      if (value) {
        query[key] = value;
      }
    });
    history.push({
      pathname: '/custom-plan-prices',
      search: qs.stringify(query),
    });
  };

  handleResetFilters = () => {
    const keys = [
      'user_profile',
      'att_plan',
      'simo_plan',
      'quick_plan',
      'is_effective',
      'is_visible',
    ];
    let isChanged = false;
    keys.forEach((key) => {
      if (this.props[key]) {
        isChanged = true;
      }
      this.props.changeField('customPlanPricingFilters', key, '');
    });
    if (isChanged) {
      this.handleApplyFilters(
        keys.reduce((acc, key) => {
          acc[key] = undefined;
          return acc;
        }, {}),
      );
    }
  };

  handleDownloadCsv = async () => {
    const {
      user_profile,
      att_plan,
      simo_plan,
      quick_plan,
      is_effective,
      is_visible,
    } = this.props.formValues;
    const filters = {
      user_profile: user_profile || undefined,
      att_plan: att_plan || undefined,
      simo_plan: simo_plan || undefined,
      quick_plan: quick_plan || undefined,
      is_effective: is_effective || undefined,
      is_visible: is_visible || undefined,
    };

    let plans = [];

    let page = 1;
    let next = 'next-url';

    this.setState({
      csvError: '',
      csvLoading: true,
    });

    while (next) {
      let response;

      try {
        response = await fetchCustomPlanPricingListRequest(
          page,
          filters,
          BIG_PAGE_SIZE,
        );
      } catch (e) {
        let messages = e.response
          ? e.response.data
          : { detail: 'Something went wrong' };
        this.setState({
          csvError: messages.messages || messages.message || messages.detail,
          csvLoading: false,
        });
      }

      plans = plans.concat(response.data.results);
      next = response.data.next;
      page += 1;
    }

    this.plansToCsv(plans);
  };

  plansToCsv = (plans) => {
    let content = '';

    let keys = [
      'id',
      'att_plan',
      'att_plan_name',
      'simo_plan',
      'simo_plan_name',
      'quick_plan',
      'quick_plan_name',
      'user_profile',
      'activation_fee',
      'price',
      'original_price',
      'is_visible',
      'is_effective',
      'effective_start_at',
      'effective_end_at',
    ];

    content += keys.map(prettyKey).join(',') + '\n';

    for (let plan of plans) {
      for (let key of keys) {
        let value = plan[key];
        if (typeof value === 'undefined') {
          value = '';
        }

        if (key === 'effective_start_at' || key === 'effective_end_at') {
          content += value ? formatDateForAPI(value) + ',' : ',';
        } else {
          content += value + ',';
        }
      }
      content = content.slice(0, -1) + '\n';
    }

    exportToCsv(`custom-plans.csv`, content);
    this.setState({
      csvLoading: false,
    });
  };

  getNextPage = (nextPage) => {
    const { user_profile, att_plan, is_effective, is_visible } = this.props;

    let params = {
      user_profile,
      att_plan,
      is_effective,
      is_visible,
      page: nextPage,
    };
    params = Object.entries(params).reduce((acc, [key, value]) => {
      if (typeof value !== 'undefined') {
        acc[key] = value;
      }
      return acc;
    }, {});

    const search = new URLSearchParams(params).toString();

    return `/custom-plan-prices?${search}`;
  };

  clearError = () => {
    this.setState({ error: null });
  };

  render() {
    const {
      customPlanPricingList,
      loggedInUser,
      pageCount,
      page,
      isPendingUpdate,
      isPendingDelete,
      user_profile,
      att_plan,
      simo_plan,
      quick_plan,
      is_effective,
      is_visible,
      loading,
    } = this.props;
    const {
      error,
      csvLoading,
      csvError,
      isUpdateModalOpen,
      updateError,
      currentCustomPlan,
    } = this.state;
    const showLoader = isPendingUpdate || isPendingDelete;
    if (!can(loggedInUser, permActions.VIEW_CUSTOM_PLAN_PRICING)) {
      return <Redirect to={'/'} />;
    }

    const canModify = can(loggedInUser, permActions.MODIFY_CUSTOM_PLAN);

    const filtersInitialValues = {
      user_profile,
      att_plan,
      is_effective,
      is_visible,
      simo_plan,
      quick_plan,
    };

    return (
      <div>
        {user_profile && (
          <p className="links">
            <Link to={`/users/${user_profile}/view`}>&larr; Back to User</Link>
          </p>
        )}
        <h1>Custom Plan Pricing</h1>
        <br />
        <div>
          {canModify && (
            <Link
              style={{ textDecoration: 'none', marginRight: '20px' }}
              to={'/custom-plan-prices/new'}
            >
              <Button>Add New</Button>
            </Link>
          )}
          <Button onClick={this.handleDownloadCsv} disabled={csvLoading}>
            Download as CSV
          </Button>
          {csvError && (
            <span style={{ paddingLeft: '20px' }} className="text-danger">
              {csvError}
            </span>
          )}
        </div>
        <br />
        {error && (
          <React.Fragment>
            <br />
            <FormError error={error} />
          </React.Fragment>
        )}
        <CustomPlanPricingFilters
          onSubmit={this.handleApplyFilters}
          onResetFilters={this.handleResetFilters}
          initialValues={filtersInitialValues}
        />
        <CustomPlanPricingList
          customPlanPricingList={customPlanPricingList}
          handleOpenDeleteModal={this.handleOpenDeleteModal}
          handleOpenUpdateModal={this.handleOpenUpdateModal}
          canModify={canModify}
        />
        <Pagination count={pageCount} current={page} url={this.getNextPage} />
        <CustomPlanPricingUpdateModal
          isOpen={isUpdateModalOpen}
          customPlanPricing={currentCustomPlan}
          onClose={this.closeUpdateModal}
          onConfirm={this.handleUpdatePlan}
          error={updateError}
        />
        <ModalContainer />
        <Spinner showLoader={showLoader || loading || csvLoading} />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  loggedInUser: getLoggedInUser(state),
  isPendingUpdate: updateCustomPlanPricingStatusPending(state),
  isPendingDelete: deleteCustomPlanPricingStatusPending(state),
  formValues: formValueSelector('customPlanPricingFilters')(
    state,
    'user_profile',
    'att_plan',
    'simo_plan',
    'quick_plan',
    'is_effective',
    'is_visible',
  ),
});

const mapDispatchToProps = {
  updateCustomPlanPricing,
  deleteCustomPlanPricing,
  fetchCustomPlanPricingList,
  changeField: change,
};

export default compose(
  withCustomPlanPricing,
  connect(mapStateToProps, mapDispatchToProps),
)(CustomPlanPricing);
